import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSessions } from '../../../actions/sessionActions';
import { getProjects } from '../../../actions/projectActions';
import './AdminAnalyticsDashboard.css';
import { FaSlidersH } from 'react-icons/fa';
import { useDashboardKPIsBackend } from '../../../hooks/useDashboardKPIsBackend';
import { calculateUsersStatsFromSessions } from '../../../utils/analyticsUtilsBackend';
import UsageCalendarHeatmapBackend from '../../analyticsBackend/UsageCalendarHeatmapBackend';
import SessionEvolutionChartBackend from '../../analyticsBackend/SessionEvolutionChartBackend';
import ToolUsageChartBackend from '../../analyticsBackend/ToolUsageChartBackend';
import ProjectsKPIBackend from '../../analyticsBackend/ProjectsKPIBackend';
import UsersTableKPIBackend from '../../analyticsBackend/UsersTableKPIBackend';
import BackendSyncAgent from '../../analyticsBackend/BackendSyncAgent';
import UsersDropdownFilter from '../../analyticsBackend/UsersDropdownFilter';

const AdminAnalyticsDashboardBackend = () => {
  const dispatch = useDispatch();
  const { isLoading, error, allSessions, allEvents } = useSelector((s) => s.sessions || {});
  const sessionsUserObj = useSelector((s) => s.sessions?.userObj);
  // Correct slice name is 'project'; get array of projects
  const projects = useSelector((s) => s.project?.projects);

  const [selectedPeriod, setSelectedPeriod] = useState('7 derniers jours');
  const [selectedSpace, setSelectedSpace] = useState('personal');
  const [durationFilter, setDurationFilter] = useState('total');
  const [showDetails, setShowDetails] = useState(false);
  const [emailFilter, setEmailFilter] = useState(''); // email selected from dropdown

  useEffect(() => {
    // Load sessions and projects for KPIs
    dispatch(getAllSessions());
    dispatch(getProjects());
  }, [dispatch]);

  const { sessionStats } = useDashboardKPIsBackend(selectedPeriod, selectedSpace, emailFilter);

  // Calculate users stats for the table
  const usersStats = useMemo(() => {
    try {
      const result = calculateUsersStatsFromSessions(allSessions, projects || [], selectedPeriod, emailFilter, sessionsUserObj);
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('[Dashboard] Error calculating users stats:', error);
      return [];
    }
  }, [allSessions, projects, selectedPeriod, emailFilter, sessionsUserObj]);

  // Dropdown selection handler
  const handleSelectUser = (email) => {
    setEmailFilter(email || '');
  };

  // Handle "Tous les utilisateurs" button
  const handleClearFilter = () => {
    setEmailFilter('');
  };

  const getFilteredDuration = () => {
    if (!sessionStats) return 0;
    switch (durationFilter) {
      case 'active': return sessionStats.averageActivityTime || 0;
      case 'inactive': return sessionStats.averageInactivityTime || 0;
      default: return sessionStats.averageDuration || 0;
    }
  };

  const getDurationTitle = () => {
    switch (durationFilter) {
      case 'active': return "Temps d'activité moyen";
      case 'inactive': return "Temps d'inactivité moyen";
      default: return 'Durée moyenne des sessions';
    }
  };

  const getFilteredTotalTime = () => {
    if (!sessionStats) return 0;
    switch (durationFilter) {
      case 'active': return sessionStats.totalActivityTime || 0;
      case 'inactive': return sessionStats.totalInactivityTime || 0;
      default: return sessionStats.totalTime || 0;
    }
  };

  const getFilteredTimesArray = () => {
    if (!sessionStats) return [];
    switch (durationFilter) {
      case 'active': return sessionStats.activityTimes || [];
      case 'inactive': return sessionStats.inactivityTimes || [];
      default: return sessionStats.sessionLengths || [];
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  return (
  <div className="analytics-dashboard admin-backend">
      <BackendSyncAgent />
      
      {/* Shell without sidebar */}
      <div className="dashboard-shell no-sidebar">
        <main className="dashboard-content">
          {/* Global filters */}
          <div className="filter-container">
            <div className="filter-card">
              <div className="filter-group">
                <div className="filter-item">
                  <FaSlidersH className="filter-icon" />
                  <span className="filter-label">Période</span>
                  <select className="filter-dropdown" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
                    <option value="Aujourd'hui">Aujourd'hui</option>
                    <option value="7 derniers jours">7 derniers jours</option>
                    <option value="30 derniers jours">30 derniers jours</option>
                    <option value="3 derniers mois">3 derniers mois</option>
                    <option value="1 dernière année">1 dernière année</option>
                  </select>
                </div>
              </div>
              <div className="filter-actions">
                <button className="filter-button secondary" onClick={handleClearFilter}>Tous les utilisateurs</button>
                <div className="search-container">
                  <UsersDropdownFilter value={emailFilter} onChange={handleSelectUser} />
                </div>
              </div>
            </div>
          </div>

          {/* KPI Cards Section - All cards at the top */}
          <section className="section">
            <div className="kpi-cards-grid">
              {/* Duration KPI Card */}
              <div className="section-card kpi-accent">
                <div className="section-header">
                  <h3 className="section-title">{getDurationTitle()}</h3>
                  <div className="card-filter">
                    <div className="card-filter-options">
                      <button className={`card-filter-option ${durationFilter === 'total' ? 'active' : ''}`} onClick={() => setDurationFilter('total')}>Total</button>
                      <button className={`card-filter-option ${durationFilter === 'active' ? 'active' : ''}`} onClick={() => setDurationFilter('active')}>Activité</button>
                      <button className={`card-filter-option ${durationFilter === 'inactive' ? 'active' : ''}`} onClick={() => setDurationFilter('inactive')}>Inactivité</button>
                      {/* Recreated details toggle button */}
                      <button
                        className={`card-filter-option ${showDetails ? 'active' : ''}`}
                        onClick={() => setShowDetails(!showDetails)}
                        title={showDetails ? 'Masquer les détails' : 'Voir les détails'}
                      >
                        Détails
                      </button>
                    </div>
                  </div>
                </div>
                <div className="number-card-value">
                  {isLoading ? 'Chargement...' : formatDuration(getFilteredDuration())}
                </div>

                {/* Inline debug removed */}

                {showDetails && !error && sessionStats && (
                  <div className="calculation-details">
                    <h4>Détails du calcul pour: {selectedPeriod}</h4>
                    <p>Nombre total de sessions: {sessionStats.totalSessions}</p>
                    <p>Temps total: {formatDuration(getFilteredTotalTime())}</p>
                    {durationFilter === 'total' && (
                      <>
                        <p>Session la plus courte: {formatDuration(sessionStats.shortestSession)}</p>
                        <p>Session la plus longue: {formatDuration(sessionStats.longestSession)}</p>
                      </>
                    )}
                    <h5>Durées individuelles (minutes):</h5>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {getFilteredTimesArray().map((length, index) => (
                        <span key={index} style={{ background: '#fff', borderRadius: 6, padding: '4px 8px', border: '1px solid #b7e0c6', fontSize: 12 }}>
                          {formatDuration(length)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sessions Count KPI Card */}
              <div className="section-card kpi-accent">
                <div className="section-header">
                  <h3 className="section-title">Nombre de sessions</h3>
                </div>
                <div className="number-card-value">
                  {isLoading ? 'Chargement...' : (sessionStats?.totalSessions || 0)}
                </div>
              </div>

              {/* Projects KPI Card */}
              <div className="section-card kpi-accent">
                <div className="section-header">
                  <h3 className="section-title">Projets</h3>
                </div>
                <ProjectsKPIBackend selectedPeriod={selectedPeriod} email={emailFilter} />
              </div>
            </div>
          </section>

          {/* Charts Section */}
          <section className="section">
            <div className="section-grid two-col">
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-title">Évolution des durées</h3>
                </div>
                <SessionEvolutionChartBackend selectedPeriod={selectedPeriod} email={emailFilter} />
              </div>
              <div className="section-card">
                <div className="section-header">
                  <h3 className="section-title">Calendrier d'utilisation</h3>
                </div>
                <UsageCalendarHeatmapBackend email={emailFilter} />
              </div>
            </div>
          </section>

          {/* Enhanced Tools and Users Section */}
          <section className="section">
            <div className="section-grid two-col">
              <div className="section-card tools-main-card">
                <div className="section-header">
                  <h3 className="section-title">Fonctionnalités les plus utilisées</h3>
                  <div className="tools-filter">
                    <select 
                      value={selectedSpace} 
                      onChange={(e) => setSelectedSpace(e.target.value)}
                      className="space-selector"
                    >
                      <option value="personal">Espace Personnel</option>
                      <option value="project">Espace Projet</option>
                      <option value="manager">Espace Manager</option>
                    </select>
                  </div>
                </div>
                <ToolUsageChartBackend selectedSpace={selectedSpace} selectedPeriod={selectedPeriod} onSpaceChange={setSelectedSpace} email={emailFilter} />
              </div>
              
              <div className="section-card">
                <UsersTableKPIBackend usersStats={usersStats} />
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default AdminAnalyticsDashboardBackend;
